import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Camera, Edit3, Save, X, User, Mail } from 'lucide-react';
import { UserProfile } from '../Profile';
import { TUserSelfPayload } from '@/data/types';

interface ProfileHeaderProps {
  userProfile: UserProfile;
  updateProfile: (data: Partial<TUserSelfPayload>) => Promise<void>;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({ userProfile, updateProfile }) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editData, setEditData] = useState({
    first_name: userProfile.first_name,
    last_name: userProfile.last_name,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  const handleSaveName = async () => {
    setIsUpdating(true);
    try {
      await updateProfile({
        first_name: editData.first_name,
        last_name: editData.last_name,
      });
      setIsEditingName(false);
    } catch (error) {
      console.error('Failed to update name:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelName = () => {
    setEditData({
      first_name: userProfile.first_name,
      last_name: userProfile.last_name,
    });
    setIsEditingName(false);
  };

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setIsUpdating(true);
      try {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const base64 = e.target?.result as string;
          await updateProfile({ image: base64 });
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Failed to update avatar:', error);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const getInitials = () => {
    return `${userProfile.first_name.charAt(0)}${userProfile.last_name.charAt(0)}`;
  };

  const getDisplayImage = () => {
    return userProfile.image_url || userProfile.avatar;
  };

  return (
    <div className="">
      {/* Banner Section */}
      <div className="relative h-48 overflow-hidden rounded-t-3xl bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600">
        {/* Banner Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/assets/energy_background.jpg)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/60 via-purple-900/50 to-pink-900/60"></div>
        </div>

        {/* Banner Edit Button */}
        <button
          onClick={() => bannerInputRef.current?.click()}
          className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-black/20 backdrop-blur-sm transition-all hover:scale-105 hover:bg-black/30"
        >
          <Camera className="h-4 w-4 text-white" />
        </button>
        <input ref={bannerInputRef} type="file" accept="image/*" className="hidden" />
      </div>

      {/* Profile Info Section */}
      <div className="relative rounded-b-3xl border border-t-0 border-gray-100/50 bg-white/80 px-8 pt-8 pb-8 shadow-xl backdrop-blur-sm">
        {/* Profile Picture - Positioned absolutely within content area */}
        <div className="absolute -top-16 left-8">
          <div className="relative inline-block">
            <div className="relative h-32 w-32 overflow-hidden rounded-full border-4 border-white bg-gradient-to-br from-blue-500 to-purple-600 shadow-xl">
              {getDisplayImage() ? (
                <img src={getDisplayImage()} alt="Profile" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-2xl font-bold text-white">
                  {getInitials()}
                </div>
              )}
              {isUpdating && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                </div>
              )}
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUpdating}
              className="absolute -right-2 -bottom-2 flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-lg transition-all hover:scale-105 hover:bg-gray-50 disabled:opacity-50"
            >
              <Camera className="h-4 w-4 text-gray-600" />
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
          </div>
        </div>

        <div className="ml-0 md:ml-40">
          {/* Name Section */}
          <div className="mb-6">
            <AnimatePresence mode="wait">
              {!isEditingName ? (
                <motion.div
                  key="name-display"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex items-center gap-3"
                >
                  <h1 className="text-3xl font-bold text-gray-900">
                    {userProfile.first_name} {userProfile.last_name}
                  </h1>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setIsEditingName(true)}
                    disabled={isUpdating}
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100/80 backdrop-blur-sm transition-all hover:bg-gray-200/80 disabled:opacity-50"
                  >
                    <Edit3 className="h-4 w-4 text-gray-600" />
                  </motion.button>
                </motion.div>
              ) : (
                <motion.div
                  key="name-edit"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-3"
                >
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={editData.first_name}
                      onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
                      className="flex-1 rounded-2xl border border-gray-200/50 bg-white/90 px-4 py-3 text-lg font-semibold text-gray-900 backdrop-blur-sm transition-all focus:border-blue-300 focus:ring-2 focus:ring-blue-100/50 focus:outline-none"
                      placeholder="Όνομα"
                      autoFocus
                      disabled={isUpdating}
                    />
                    <input
                      type="text"
                      value={editData.last_name}
                      onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
                      className="flex-1 rounded-2xl border border-gray-200/50 bg-white/90 px-4 py-3 text-lg font-semibold text-gray-900 backdrop-blur-sm transition-all focus:border-blue-300 focus:ring-2 focus:ring-blue-100/50 focus:outline-none"
                      placeholder="Επώνυμο"
                      disabled={isUpdating}
                    />
                  </div>
                  <div className="flex gap-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleSaveName}
                      disabled={isUpdating}
                      className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-lg transition-all hover:bg-blue-700 disabled:opacity-50"
                    >
                      {isUpdating ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      Αποθήκευση
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleCancelName}
                      disabled={isUpdating}
                      className="flex items-center gap-2 rounded-xl bg-gray-200/80 px-4 py-2 text-sm font-medium text-gray-700 backdrop-blur-sm transition-all hover:bg-gray-300/80 disabled:opacity-50"
                    >
                      <X className="h-4 w-4" />
                      Ακύρωση
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* User Info */}
          <div className="mb-6 flex flex-wrap items-center gap-4 text-gray-600">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span className="font-medium">
                {userProfile.username ? `@${userProfile.username}` : 'Μη καθορισμένο όνομα χρήστη'}
              </span>
            </div>
            <span className="text-gray-400">•</span>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <span className="font-medium text-blue-600">{userProfile.email}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
